import { useEffect, useState } from 'react';
import { fetchOroUniverseTape, type OroTapeRow } from '../api/market';
import {
  fetchOroborosTopBacktests,
  mergeOroborosSparklineRows,
  type OroTopBacktestRow,
} from '../api/strategies';

export type LandingTapeRow = {
  coin: string;
  mid: number | null;
  changePct: number | null;
};

export type LandingLiveStatus = 'loading' | 'live' | 'offline';

/** Majors first so the tape reads the same on every load; the rest fill by volume. */
const TAPE_PREFERRED = ['BTC', 'ETH', 'SOL', 'HYPE', 'XRP', 'DOGE', 'SUI', 'BNB', 'LINK', 'AVAX', 'ARB', 'OP'];
const TAPE_SIZE = 14;
const TAPE_REFRESH_MS = 20_000;
const STRATEGIES_TIMEOUT_MS = 15_000;
const STRATEGIES_LIMIT = 8;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const t = window.setTimeout(() => resolve(null), ms);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      () => {
        window.clearTimeout(t);
        resolve(null);
      },
    );
  });
}

export function pickTapeRows(markets: OroTapeRow[]): LandingTapeRow[] {
  const perps = markets.filter((m) => m.kind === 'perp');
  const byCoin = new Map(perps.map((m) => [m.coin.toUpperCase(), m]));
  const out: LandingTapeRow[] = [];
  const seen = new Set<string>();
  for (const coin of TAPE_PREFERRED) {
    const m = byCoin.get(coin);
    if (!m) continue;
    seen.add(coin);
    out.push({ coin, mid: m.mid, changePct: m.changePct });
  }
  for (const m of perps) {
    if (out.length >= TAPE_SIZE) break;
    const coin = m.coin.toUpperCase();
    if (seen.has(coin)) continue;
    seen.add(coin);
    out.push({ coin, mid: m.mid, changePct: m.changePct });
  }
  return out;
}

/**
 * Signed-out landing proof: perpetual tape (direct venue fetch, no core dependency)
 * plus the public ranked backtest board. Both degrade to hidden, never to an error.
 */
export function useOroborosLandingLive() {
  const [tape, setTape] = useState<LandingTapeRow[]>([]);
  const [tapeStatus, setTapeStatus] = useState<LandingLiveStatus>('loading');
  const [marketCount, setMarketCount] = useState(0);
  const [strategies, setStrategies] = useState<OroTopBacktestRow[]>([]);
  const [strategiesStatus, setStrategiesStatus] = useState<LandingLiveStatus>('loading');

  useEffect(() => {
    let cancelled = false;

    const loadTape = async () => {
      try {
        // Raw HL tape (direct to api.hyperliquid.xyz) — never blocked on core being up.
        const markets = await fetchOroUniverseTape({ includeHip3: false });
        if (cancelled) return;
        const rows = pickTapeRows(markets);
        const hasPrices = rows.some((r) => r.mid != null);
        setTape(rows);
        setMarketCount(markets.filter((m) => m.kind === 'perp').length);
        setTapeStatus(hasPrices ? 'live' : 'offline');
      } catch {
        if (!cancelled) setTapeStatus('offline');
      }
    };

    void loadTape();
    const id = window.setInterval(() => void loadTape(), TAPE_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const fast = await withTimeout(
        fetchOroborosTopBacktests({ filter: 'good', limit: STRATEGIES_LIMIT }),
        STRATEGIES_TIMEOUT_MS,
      );
      if (cancelled) return;
      if (!fast?.success || !fast.rows.length) {
        setStrategiesStatus('offline');
        return;
      }
      setStrategies(fast.rows);
      setStrategiesStatus('live');

      const withSpark = await withTimeout(
        fetchOroborosTopBacktests({ filter: 'good', limit: STRATEGIES_LIMIT, sparkline: true }),
        STRATEGIES_TIMEOUT_MS,
      );
      if (cancelled || !withSpark?.success) return;
      setStrategies((cur) => mergeOroborosSparklineRows(cur, withSpark.rows));
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { tape, tapeStatus, marketCount, strategies, strategiesStatus };
}
